import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Alert } from 'react-native';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { stripeConfigService } from '../../services/StripeConfigService';

const StripeConfigToggle = () => {
    const [currentMode, setCurrentMode] = useState('test');
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const db = getFirestore();

    // Load current configuration
    useEffect(() => {
        loadCurrentConfig();
    }, []);

    const loadCurrentConfig = async () => {
        console.log('Stripe Config: Loading current configuration...');
        try {
            setLoading(true);
            const mode = await stripeConfigService.getStripeMode();
            console.log(`Stripe Config: Loaded mode from service: ${mode}`);
            setCurrentMode(mode);
        } catch (error) {
            console.error('Error loading Stripe configuration:', error);
            window.alert('Error: Failed to load current configuration');
        } finally {
            setLoading(false);
        }
    };

    const updateStripeMode = async (newMode) => {
        console.log(`Stripe Config: Starting update to ${newMode} mode`);
        
        try {
            setUpdating(true);
            
            // Update configuration using the service
            console.log('Stripe Config: Calling setStripeMode service...');
            await stripeConfigService.setStripeMode(newMode);
            console.log('Stripe Config: Service call completed successfully');

            // Update local state
            setCurrentMode(newMode);
            console.log(`Stripe Config: Local state updated to ${newMode}`);
            
            // Force refresh of all Stripe components by clearing cache
            stripeConfigService.clearCache();
            console.log('Stripe Config: Cache cleared');
            
            window.alert(`Success! Stripe mode updated to ${newMode.toUpperCase()}. All payment processing will now use ${newMode} keys. Page refresh recommended for immediate effect.`);
        } catch (error) {
            console.error('Error updating Stripe configuration:', error);
            window.alert('Error: Failed to update Stripe configuration: ' + error.message);
        } finally {
            setUpdating(false);
        }
    };

    const handleModeChange = (newMode) => {
        console.log(`Stripe Config: Button clicked for ${newMode} mode`);
        
        // Use native confirm dialog for web compatibility
        const confirmed = window.confirm(
            `Are you sure you want to switch to ${newMode.toUpperCase()} mode?\n\nThis will affect all payment processing globally for all users.`
        );
        
        if (confirmed) {
            updateStripeMode(newMode);
        } else {
            console.log('Stripe Config: Mode change cancelled by user');
        }
    };

    if (loading) {
        return (
            <View style={styles.container}>
                <Text style={styles.title}>Stripe Configuration</Text>
                <Text style={styles.loadingText}>Loading current configuration...</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <Text style={styles.title}>Stripe Configuration</Text>
            <Text style={styles.subtitle}>
                Current Mode: <Text style={styles.currentMode}>{currentMode.toUpperCase()}</Text>
            </Text>
            <Text style={styles.description}>
                Toggle between test and live Stripe payment processing modes. 
                This setting affects all users globally.
            </Text>
            
            <View style={styles.buttonContainer}>
                <Pressable
                    style={[
                        styles.button,
                        styles.testButton,
                        currentMode === 'test' && styles.activeButton,
                        updating && styles.disabledButton
                    ]}
                    onPress={() => handleModeChange('test')}
                    onPressIn={() => console.log('TEST button pressed in')}
                    disabled={updating || currentMode === 'test'}
                >
                    <Text style={[
                        styles.buttonText,
                        currentMode === 'test' && styles.activeButtonText
                    ]}>
                        TEST MODE
                    </Text>
                    {currentMode === 'test' && <Text style={styles.activeIndicator}>ACTIVE</Text>}
                </Pressable>
                
                <Pressable
                    style={[
                        styles.button,
                        styles.liveButton,
                        currentMode === 'live' && styles.activeButton,
                        updating && styles.disabledButton
                    ]}
                    onPress={() => handleModeChange('live')}
                    onPressIn={() => console.log('LIVE button pressed in')}
                    disabled={updating || currentMode === 'live'}
                >
                    <Text style={[
                        styles.buttonText,
                        currentMode === 'live' && styles.activeButtonText
                    ]}>
                        LIVE MODE
                    </Text>
                    {currentMode === 'live' && <Text style={styles.activeIndicator}>ACTIVE</Text>}
                </Pressable>
            </View>
            
            {updating && (
                <Text style={styles.updatingText}>Updating configuration...</Text>
            )}
            
            <View style={styles.warningContainer}>
                <Text style={styles.warningText}>
                    ⚠️ Warning: Changing to LIVE mode will process real payments. 
                    Only use LIVE mode in production.
                </Text>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#f8f9fa',
        padding: 20,
        margin: 10,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#212529',
    },
    subtitle: {
        fontSize: 16,
        marginBottom: 12,
        color: '#495057',
    },
    currentMode: {
        fontWeight: 'bold',
        color: '#007bff',
    },
    description: {
        fontSize: 14,
        color: '#6c757d',
        marginBottom: 20,
        lineHeight: 20,
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    button: {
        flex: 1,
        paddingVertical: 16,
        paddingHorizontal: 16,
        marginHorizontal: 8,
        borderRadius: 8,
        alignItems: 'center',
        borderWidth: 2,
    },
    testButton: {
        backgroundColor: '#fff3cd',
        borderColor: '#ffc107',
    },
    liveButton: {
        backgroundColor: '#f8d7da',
        borderColor: '#dc3545',
    },
    activeButton: {
        backgroundColor: '#007bff',
        borderColor: '#007bff',
    },
    disabledButton: {
        opacity: 0.6,
    },
    buttonText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#495057',
    },
    activeButtonText: {
        color: '#ffffff',
    },
    activeIndicator: {
        fontSize: 12,
        color: '#ffffff',
        marginTop: 4,
        fontWeight: 'bold',
    },
    loadingText: {
        fontSize: 16,
        color: '#6c757d',
        textAlign: 'center',
    },
    updatingText: {
        fontSize: 14,
        color: '#007bff',
        textAlign: 'center',
        marginBottom: 12,
        fontStyle: 'italic',
    },
    warningContainer: {
        backgroundColor: '#fff3cd',
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#ffc107',
    },
    warningText: {
        fontSize: 12,
        color: '#856404',
        textAlign: 'center',
        fontWeight: '500',
    },
});

export default StripeConfigToggle;